// =============================================================================
// UPDATED PROTECTED ROUTE - frontend/src/components/ProtectedRoute.tsx
// =============================================================================

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'owner' | 'manager' | 'employee';
  allowedRoles?: string[];
  fallbackPath?: string;
}

export default function ProtectedRoute({
  children,
  requiredRole,
  allowedRoles = [],
  fallbackPath = '/login'
}: ProtectedRouteProps) {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user, checkAuth } = useAuth();
  const location = useLocation();

  // Re-verify auth when route changes
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      console.log('🔐 ProtectedRoute: User not authenticated, checking auth...');
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('verifying_auth')}</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log('🔓 ProtectedRoute: User not authenticated, redirecting to login');
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // ✅ SUBSCRIPTION ENFORCEMENT
  if (user && (user.role === 'owner' || user.role === 'admin')) {
    // Skip check for billing page itself to avoid infinite redirect
    if (location.pathname !== '/billing' && location.pathname !== '/login') {

      const plan = user.tenant_plan || 'trial';
      const status = user.tenant_status || 'active';
      const createdAt = user.tenant_created_at ? new Date(user.tenant_created_at) : new Date();

      // Check for inactive status
      if (status === 'inactive') {
        console.log('🔒 Account inactive, redirecting to billing');
        return <Navigate to="/billing?reason=inactive" replace />;
      }

      // Check for expired trial
      // FIXED: Don't block if status is 'trialing' (valid Stripe trial) or 'active'
      const isManualTrial = (plan === 'trial' || plan === 'TRIAL') && status !== 'trialing' && status !== 'active';

      if (isManualTrial) {
        const trialDays = 14;
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - createdAt.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > trialDays) {
          console.log('🔒 Trial expired (… days), redirecting to billing');
          return <Navigate to="/billing?reason=trial_expired" replace />;
        }
      }

      // Check for missing plan (shouldn't happen with defaults but good for safety)
      if (!plan || plan === 'none') {
        console.log('🔒 No plan selected, redirecting to billing');
        return <Navigate to="/billing?reason=no_plan" replace />;
      }
    }
  }

  // ✅ ROLE-BASED ACCESS CONTROL
  if (user) {
    const userRole = user.role;

    // Check required role
    if (requiredRole) {
      const hasRequiredRole = checkUserRole(userRole, requiredRole);

      if (!hasRequiredRole) {
        console.log('🚫 ProtectedRoute: User role \'…\' insufficient for required role \'…\'');

        // ✅ EMPLOYEE-SPECIFIC REDIRECTS
        if (userRole === 'employee') {
          // Employees can only access tasks page
          if (location.pathname !== '/announcements') {
            console.log('🔄 Redirecting employee to announcements page');
            return <Navigate to="/announcements" replace />;
          }
        }

        // ✅ PROPERTY OWNER SPECIFIC REDIRECTS
        if (userRole === 'property_owner') {
          if (location.pathname !== '/announcements') {
            console.log('🔄 Redirecting property owner to announcements page');
            return <Navigate to="/announcements" replace />;
          }
        }

        // Show access denied for current page
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center max-w-md mx-auto">
              <div className="bg-red-100 rounded-full p-3 mx-auto w-16 h-16 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('access_denied')}</h2>
              <p className="text-gray-600 mb-4">
                {t('access_denied_desc', { role: userRole })}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {t('required_role', { role: requiredRole })}
              </p>

              {/* ✅ ROLE-BASED REDIRECT BUTTONS */}
              {userRole === 'employee' ? (
                <button
                  onClick={() => window.location.href = '/announcements'}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('announcements_feed_title', 'Avisos y noticias')}
                </button>
              ) : userRole === 'property_owner' ? (
                <button
                  onClick={() => window.location.href = '/announcements'}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('announcements_feed_title', 'Avisos y noticias')}
                </button>
              ) : (
                <button
                  onClick={() => window.location.href = '/announcements'}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('announcements_feed_title', 'Avisos y noticias')}
                </button>
              )}
            </div>
          </div>
        );
      }
    }

    // Check allowed roles array
    if (allowedRoles.length > 0) {
      const hasAllowedRole = allowedRoles.includes(userRole);

      if (!hasAllowedRole) {
        console.log('🚫 ProtectedRoute: User role \'…\' not in allowed roles: …');

        // ✅ ROLE REDIRECTS
        if (userRole === 'employee') {
          return <Navigate to="/announcements" replace />;
        }
        if (userRole === 'property_owner') {
          return <Navigate to="/announcements" replace />;
        }
        return <Navigate to="/announcements" replace />; // Default fallback for others
      }
    }
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
}

// ✅ UPDATED Helper function to check user roles
function checkUserRole(userRole: string, requiredRole: string): boolean {
  const roleHierarchy = {
    'admin': 4,
    'owner': 3,
    'manager': 2,
    'employee': 1,
    'property_owner': 1 // Same level as employee for now
  };

  const userLevel = roleHierarchy[userRole as keyof typeof roleHierarchy] || 0;
  const requiredLevel = roleHierarchy[requiredRole as keyof typeof roleHierarchy] || 0;

  return userLevel >= requiredLevel;
}

// ✅ UPDATED Specialized components for different role requirements
export function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['admin']}>
      {children}
    </ProtectedRoute>
  );
}

export function ManagerRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}>
      {children}
    </ProtectedRoute>
  );
}

export function EmployeeRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee']}>
      {children}
    </ProtectedRoute>
  );
}

export function PropertyOwnerRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['property_owner', 'admin', 'owner']}>
      {children}
    </ProtectedRoute>
  );
}
